// 0068 — Multi-line "Add Plan" deal pricing. SINGLE SOURCE OF TRUTH shared by
// the React deal builder (live preview) and the Express batch endpoint
// (persisted proforma) so the on-screen total === the stored total.
//
// Pricing model (confirmed with operator):
//   lineMonthly = unitPrice(per-unit plan price) × qty × (1 − lineDiscountPct/100)
//   Recurring line:
//     stub = prorated days from startDate → next billing-day anchor (ADDED ON TOP)
//     lineTaxable = stub + durationMonths × lineMonthly
//   Non-recurring line:
//     lineTaxable = qty × unitPrice × (1 − lineDiscountPct/100)   // one-time
//   Then overall additional-discount % and payment-mode % apply to the SUMMED
//   subtotal; the discounted total is distributed back per line; GST is split
//   per line (plan.taxRatePct ?? 18) via deriveGstSplit and summed into the header.
//
// All money rounds to 2dp at line boundaries identically on both sides.

import { deriveGstSplit } from "./gst-split.js";

export type PaymentMode = "Cash" | "UPI" | "Bank_Transfer" | "Card" | "Cheque";

/**
 * Per-payment-mode auto discount %. All 0 for now — wired so the percentages
 * can change later in ONE place (UI + server both read this).
 */
export const PAYMENT_MODE_DISCOUNT_PCT: Record<PaymentMode, number> = {
  Cash: 0,
  UPI: 0,
  Bank_Transfer: 0,
  Card: 0,
  Cheque: 0,
};

/**
 * GST rate applied when a plan declares no taxRatePct (NULL). A plan with no
 * tax rate configured is treated as GST-exempt: no GST is added to its
 * receipts/invoices/quotes. Set an explicit taxRatePct on the plan to charge GST.
 */
export const DEFAULT_GST_RATE = 0;

export interface QuoteLineInput {
  planId: number;
  description: string;
  /** Per-unit plan price (plan.monthlyPrice). */
  unitPrice: number;
  qty: number;
  /** Committed months — used only for recurring lines. */
  durationMonths: number;
  lineDiscountPct: number;
  /** plan.taxRatePct; the helper applies `?? DEFAULT_GST_RATE`. */
  taxRatePct: number | null;
  /** true when plan.cadence === "Recurring" (drives proration + monthly multiply). */
  recurring: boolean;
  /**
   * Refundable security deposit for the WHOLE line, already resolved by the
   * caller (matrix tier amount, or plan baseline × qty). NOT discounted/taxed —
   * added to the grand total as-is.
   */
  securityDepositLineTotal: number;
  /**
   * 0132 — Sunday access add-on, PER UNIT PER MONTH (0 / omitted when off).
   * Multiplied by qty and run through the SAME term/proration maths as rent, so
   * a part-month first stub charges a part-month of Sunday too.
   *
   * It is TAXED like rent but deliberately NOT discounted: the Additional
   * Discount % is a negotiated reduction on rent, while this is a published flat
   * service rate. Keeping it out of the discount base also keeps it out of
   * `frozen_discounted_price`, which is reverse-derived back into a discount %
   * elsewhere and would erase it.
   */
  sundayAccessMonthly?: number;
}

export interface QuotePricingInput {
  lines: QuoteLineInput[];
  /** YYYY-MM-DD. */
  startDate: string;
  /**
   * YYYY-MM-DD or null. When set, the plan has ENDED: recurring lines bill only
   * the prorated actual run (startDate → endDate) instead of a forward stub +
   * durationMonths, and they don't count toward the recurring monthly total.
   */
  endDate?: string | null;
  /** 1..31 (clamped inside; short months collapse to their last day). */
  billingCycleDay: number;
  /**
   * When false, the recurring first-period proration STUB is omitted, so a line
   * bills whole months only (durationMonths × monthly). Defaults to true — the
   * Add-Plan quote behaviour. View Plan passes false so its summary matches the
   * ACTUAL recurring billing, which charges flat monthly cycles with no stub.
   */
  prorateFirstPeriod?: boolean;
  additionalDiscountPct: number;
  paymentMode: PaymentMode;
  gstApplicable: boolean;
  /** Space GST state code (e.g. "07"). */
  issuerStateCode: string;
  /** Account place-of-supply; null/equal → intra-state CGST+SGST. */
  memberStateCode: string | null;
}

export interface QuoteLineResult {
  planId: number;
  description: string;
  quantity: number;
  durationMonths: number;
  unitPrice: number;
  lineDiscountPct: number;
  /** Net recurring monthly for this line = qty × unitPrice × (1 − discount). One-time amount for non-recurring. */
  lineMonthly: number;
  /** Prorated first-stub (pre overall-discount); 0 if start on billing day / non-recurring. */
  proratedFirstAmount: number;
  /** Taxable after per-line + overall discounts (what GST is computed on). */
  taxableAmount: number;
  gstRate: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  /** Refundable security deposit for this line (qty × per-unit), non-taxable. */
  securityDeposit: number;
  /** 0132 — net monthly Sunday fee for this line (qty × per-unit). 0 when off. */
  sundayAccessMonthly: number;
  /**
   * 0132 — the Sunday portion of `taxableAmount` over the whole term (already
   * included in it). Undiscounted. Receipts use this to emit a separate line.
   */
  sundayAccessTaxable: number;
  /** Rental portion of the line (taxable + GST); excludes the deposit. */
  lineTotal: number;
}

export interface QuotePricingResult {
  lines: QuoteLineResult[];
  /** Sum of pre-overall-discount line taxables (AFTER the per-line/plan discount). */
  subtotal: number;
  /** Sum of line taxables at full price (BEFORE the per-line/plan discount). */
  grossSubtotal: number;
  /** Rupee value of the per-line/plan (matrix) discount = grossSubtotal − subtotal. */
  planDiscountTotal: number;
  /** Effective blended plan-discount % (planDiscountTotal / grossSubtotal). */
  planDiscountPct: number;
  additionalDiscountPct: number;
  additionalDiscountAmount: number;
  paymentModeDiscountPct: number;
  paymentModeDiscountAmount: number;
  /** Total of the two overall discounts (per-line discount already in subtotal). */
  totalDiscount: number;
  /** Taxable base after all discounts (== sum of per-line taxableAmount). */
  taxableAfterDiscounts: number;
  cgstTotal: number;
  sgstTotal: number;
  igstTotal: number;
  /** Sum of all line security deposits (refundable, non-taxable). */
  securityDepositTotal: number;
  /**
   * 0132 — total undiscounted, taxable Sunday access across all lines. Already
   * folded into `taxableAfterDiscounts` (and so into the GST and grand total);
   * surfaced separately so the quote can show it on its own row.
   */
  sundayAccessTotal: number;
  /** Recurring monthly total the system will bill (sum of recurring lines' net monthly). */
  monthlyRecurringTotal: number;
  /** Exact GST-inclusive payable (incl. security deposit), 2-dp. Kept for back-compat. */
  grandTotal: number;
  /**
   * Nearest-rupee round-off applied to grandTotal (signed, 2-dp).
   * = roundedGrandTotal − grandTotal. e.g. 42480.29 → −0.29; *.95 → +0.05.
   * Document/estimate-level presentation only — the component figures (taxable,
   * CGST/SGST/IGST, deposit) are UNCHANGED.
   */
  roundOff: number;
  /** grandTotal rounded to the nearest rupee (.5 up). The printed final payable. */
  roundedGrandTotal: number;
}

/**
 * Nearest-rupee round-off for a GST-inclusive total `T` (2-dp). Returns the
 * rounded total and the signed adjustment (roundedTotal − T). SINGLE SOURCE used
 * by the quote, the per-period order receipt / tax invoice, and the bundle docs
 * so the same ≤50-paise adjustment is shown everywhere. Presentation-only: never
 * mutate stored period amounts with this.
 */
export function computeRoundOff(total: number): { roundedTotal: number; roundOff: number } {
  const roundedTotal = Math.round(total);
  const roundOff = round2(roundedTotal - total);
  return { roundedTotal, roundOff };
}

export function round2(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export interface ListGrossUp {
  /** PRICE/MO — the list rate when a discount exists, else the billed rate. */
  unitPrice: number;
  /** AMOUNT — the list equivalent when a discount exists, else the billed taxable. */
  lineTotal: number;
  /** list − billed for this line; 0 when no discount is recorded. */
  discountAmount: number;
}

/**
 * Recover a receipt line's PRE-DISCOUNT (list) figures so a document can print
 * "Subtotal … Discount − X … GST" instead of silently folding the discount into
 * the rate.
 *
 * Per the pricing model at the top of this file, `memberships.unit_price` is
 * the list rate snapshot taken at deal creation and `frozen_discounted_price`
 * the post-discount rate actually billed — including any additional-discount %
 * distributed back per line. The RATIO between them therefore scales any billed
 * figure to its list equivalent: a full cycle, a prorated stub, or an
 * operator-overridden period alike. Scaling rather than recomputing matters —
 * the per-period receipt derives its taxable from the PERSISTED period amount,
 * and re-deriving would discard that override.
 *
 * GST is unaffected (it stays computed on the billed taxable), so the printed
 * TOTAL is identical whether or not a discount is recorded. The invariant
 * callers rely on is `Σ lineTotal − Σ discountAmount === Σ billed taxable`.
 *
 * Returns the billed figures untouched (discount 0) when no list rate is
 * recorded, when the two rates are equal, or when the "list" rate is the LOWER
 * of the two. That last case is real: the restructure path writes the
 * discounted price into BOTH columns, so those memberships show no discount
 * rather than a negative one. A discount is never invented.
 */
export function listGrossUp(opts: {
  listPerUnit: number | null | undefined;
  billedPerUnit: number;
  displayUnitPrice: number;
  billedTaxable: number;
}): ListGrossUp {
  const list = opts.listPerUnit == null ? 0 : Number(opts.listPerUnit);
  const billed = Number(opts.billedPerUnit);
  if (!Number.isFinite(list) || !(billed > 0) || !(list > billed)) {
    return {
      unitPrice: opts.displayUnitPrice,
      lineTotal: opts.billedTaxable,
      discountAmount: 0,
    };
  }
  const factor = list / billed;
  const lineTotal = round2(opts.billedTaxable * factor);
  return {
    unitPrice: round2(opts.displayUnitPrice * factor),
    lineTotal,
    discountAmount: round2(lineTotal - opts.billedTaxable),
  };
}

// Widened from 28 → 31 on 2026-09-29 so members starting on 29/30/31 don't
// force a prorated-stub period every month. Mirrors artifacts/api-server's
// lib/plan-schedule.ts:clampBillingDay. Short-month safety comes from
// `anchorDayInMonth` below (matches the same helper in plan-schedule.ts).
function clampBillingDay(day: number): number {
  if (!Number.isFinite(day)) return 1;
  return Math.min(31, Math.max(1, Math.trunc(day)));
}

/** Calendar days in the month of a YYYY-MM-DD date. */
function daysInMonthOf(isoDate: string): number {
  const [y, m] = isoDate.split("-").map(Number);
  if (!y || !m) return 30;
  return new Date(y, m, 0).getDate(); // day 0 of next month = last day of month m
}

// Resolve the anchor day within a specific calendar month; caps to the month's
// last day when the anchor doesn't exist there (April 31 → 30, Feb 29 non-leap
// → 28). Kept private to this file so the widened clamp can't leak invalid
// dates into stub-day arithmetic below.
function anchorDayInMonth(year: number, month1based: number, anchor: number): number {
  const lastDay = new Date(year, month1based, 0).getDate();
  return Math.min(anchor, lastDay);
}

/**
 * Days from `startDate` up to (not including) the next date whose day-of-month
 * equals the billing anchor. billingDay is clamped to 1..31; for short months
 * the anchor collapses to the month's last day (so anchor=31 in April behaves
 * as if the operator picked 30). Returns 0 when start is already on the anchor.
 */
function stubDaysToAnchor(startDate: string, billingDay: number): number {
  const [yStr, mStr, dStr] = startDate.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const startDay = Number(dStr);
  const rawAnchor = clampBillingDay(billingDay);
  const sameMonthAnchor = anchorDayInMonth(y, m, rawAnchor);
  if (startDay === sameMonthAnchor) return 0;
  if (sameMonthAnchor > startDay) return sameMonthAnchor - startDay;
  // Next month — re-resolve the anchor within its actual length.
  const nextYear = m === 12 ? y + 1 : y;
  const nextMonth = m === 12 ? 1 : m + 1;
  const nextMonthAnchor = anchorDayInMonth(nextYear, nextMonth, rawAnchor);
  return daysInMonthOf(startDate) - startDay + nextMonthAnchor;
}

/** Add whole months to YYYY-MM-DD, clamping the day to the target month's last day. */
function addMonthsISO(iso: string, months: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const base = new Date(Date.UTC(y!, (m! - 1) + months, 1));
  const year = base.getUTCFullYear();
  const month = base.getUTCMonth();
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(d!, lastDay);
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Whole-day difference b − a for two YYYY-MM-DD dates. */
function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round((Date.UTC(by!, bm! - 1, bd!) - Date.UTC(ay!, am! - 1, ad!)) / 86_400_000);
}

function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d! + days));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

/**
 * Taxable for an ENDED recurring line = the prorated actual run from start → end,
 * where `end` is the INCLUSIVE last day of the run. A full billing cycle is
 * [start, start+1mo−1day] (e.g. 17 Apr → 16 May = one whole month). Whole months
 * at the monthly rate + a day-prorated inclusive remainder.
 */
export function endedRunMonths(start: string, end: string): number {
  if (daysBetween(start, end) < 0) return 0;
  let months = 0;
  let cursor = start;
  while (daysBetween(addDaysISO(addMonthsISO(cursor, 1), -1), end) >= 0) {
    months++;
    cursor = addMonthsISO(cursor, 1);
  }
  // Any remaining part-month still contains its Sundays, so it counts as one.
  const remDays = Math.max(0, daysBetween(cursor, end) + 1);
  return months + (remDays > 0 ? 1 : 0);
}

export function endedRunTaxable(start: string, end: string, monthly: number): number {
  if (daysBetween(start, end) < 0) return 0;
  let months = 0;
  let cursor = start;
  // A whole month [cursor, cursor+1mo−1day] is complete when its last day <= end.
  while (daysBetween(addDaysISO(addMonthsISO(cursor, 1), -1), end) >= 0) {
    months++;
    cursor = addMonthsISO(cursor, 1);
  }
  // Remaining partial days are inclusive of the end date.
  const remDays = Math.max(0, daysBetween(cursor, end) + 1);
  const remMonthDays = daysInMonthOf(cursor);
  return round2(months * monthly + (monthly / remMonthDays) * remDays);
}

export function computeQuotePricing(input: QuotePricingInput): QuotePricingResult {
  const {
    lines,
    startDate,
    billingCycleDay,
    additionalDiscountPct,
    paymentMode,
    gstApplicable,
    issuerStateCode,
    memberStateCode,
  } = input;

  // ── 1. Per-line raw taxable (pre overall-discount) + stub ────────────────
  // Compute both the net (after per-line/plan discount) and the gross (full
  // price) so the plan discount can be shown as its own summary line item.
  const raw = lines.map((l) => {
    const qty = Math.max(1, l.qty || 1);
    const disc = l.lineDiscountPct || 0;
    const lineMonthlyGross = round2(l.unitPrice * qty);
    const lineMonthly = round2(lineMonthlyGross * (1 - disc / 100));

    // 0132 — the Sunday add-on rides the SAME term maths as rent (ended-run
    // proration, first-stub, months), so it is extracted into a helper rather
    // than duplicated three times. Behaviour for rent is unchanged.
    const termTaxable = (monthly: number): { taxable: number; stub: number } => {
      if (l.recurring && input.endDate) {
        // Ended plan: bill only the prorated actual run (start → end). No forward
        // stub, no durationMonths projection.
        return { taxable: endedRunTaxable(startDate, input.endDate, monthly), stub: 0 };
      }
      if (l.recurring) {
        const months = Math.max(1, l.durationMonths || 1);
        // The forward first-period stub is an Add-Plan quote concept; callers that
        // reflect actual recurring billing (View Plan) opt out via prorateFirstPeriod.
        const sd =
          input.prorateFirstPeriod === false ? 0 : stubDaysToAnchor(startDate, billingCycleDay);
        const dim = daysInMonthOf(startDate);
        const s0 = sd > 0 ? round2((monthly / dim) * sd) : 0;
        // 2026-06-29: the stub IS the first cycle, not an extra cycle layered on
        // top of `months`. Operator sets durationMonths = "how many cycles total
        // the customer is committing to"; if start ≠ billing day, the first
        // cycle is the prorated stub and the remaining (months − 1) cycles
        // are full monthly amounts. Old formula `stub + months × lineMonthly`
        // double-counted the first cycle: Period 1 ended up billing stub + a
        // full month, ~2 months of service, surprising the operator.
        const fullMonthsAfterStub = Math.max(0, sd > 0 ? months - 1 : months);
        return { taxable: round2(s0 + fullMonthsAfterStub * monthly), stub: s0 };
      }
      // Non-recurring: one-time charge, no months / proration.
      return { taxable: monthly, stub: 0 };
    };

    const net = termTaxable(lineMonthly);
    const gross = termTaxable(lineMonthlyGross);

    // Per-unit-per-month × qty, exactly like rent. Never discounted.
    const sundayMonthly = round2(Math.max(0, l.sundayAccessMonthly || 0) * qty);
    // ⚠ WHOLE MONTHS, never day-prorated — unlike rent. Operator's rule
    // (2026-10-23): Sunday access is a monthly entitlement, so a part-month first
    // period still contains its Sundays and is charged in full.
    //
    // This has a second, load-bearing benefit. The amount has NO date input, so
    // it is identical however the caller anchors the line. That matters because
    // add-plan.tsx prices each line with its OWN startDate/billingCycleDay while
    // memberships.ts prices the whole batch with line 1's — a known divergence
    // that already makes rent differ between the screen and the saved proforma
    // for lines 2..N. A day-prorated add-on would inherit it; this one cannot.
    const sundayMonths = !l.recurring
      ? 0
      : input.endDate
        ? endedRunMonths(startDate, input.endDate)
        : Math.max(1, l.durationMonths || 1);
    const sundayTaxable = round2(sundayMonthly * sundayMonths);

    return {
      line: l,
      qty,
      stub: net.stub,
      lineMonthly,
      rawTaxable: net.taxable,
      grossTaxable: gross.taxable,
      sundayMonthly,
      sundayTaxable,
    };
  });

  const subtotal = round2(raw.reduce((s, r) => s + r.rawTaxable, 0));
  // Sits OUTSIDE the discount base below — added back after distribution, before GST.
  const sundayAccessTotal = round2(raw.reduce((s, r) => s + r.sundayTaxable, 0));
  const grossSubtotal = round2(raw.reduce((s, r) => s + r.grossTaxable, 0));
  const planDiscountTotal = round2(grossSubtotal - subtotal);
  const planDiscountPct =
    grossSubtotal > 0 ? round2((planDiscountTotal / grossSubtotal) * 100) : 0;

  // ── 2. Overall discounts on the summed subtotal ──────────────────────────
  const addlPct = additionalDiscountPct || 0;
  const modePct = PAYMENT_MODE_DISCOUNT_PCT[paymentMode] ?? 0;
  const afterAddl = round2(subtotal * (1 - addlPct / 100));
  const afterMode = round2(afterAddl * (1 - modePct / 100));
  const additionalDiscountAmount = round2(subtotal - afterAddl);
  const paymentModeDiscountAmount = round2(afterAddl - afterMode);
  const discountFactor = subtotal > 0 ? afterMode / subtotal : 1;

  // ── 3. Distribute discounted taxable back per line (fix residual on last) ─
  const discounted = raw.map((r) => round2(r.rawTaxable * discountFactor));
  const distributedSum = round2(discounted.reduce((s, n) => s + n, 0));
  const residual = round2(afterMode - distributedSum);
  if (discounted.length > 0 && residual !== 0) {
    discounted[discounted.length - 1] = round2(
      discounted[discounted.length - 1]! + residual,
    );
  }

  // ── 4. GST split per line + refundable deposit, summed into header ───────
  let cgstTotal = 0;
  let sgstTotal = 0;
  let igstTotal = 0;
  let securityDepositTotal = 0;
  let monthlyRecurringTotal = 0;

  const resultLines: QuoteLineResult[] = raw.map((r, i) => {
    // The discounted rent share plus this line's UNdiscounted Sunday fee. GST is
    // charged on the sum, so the add-on is taxed exactly like rent.
    const taxableAmount = round2(discounted[i]! + r.sundayTaxable);
    const gstRate = gstApplicable
      ? r.line.taxRatePct ?? DEFAULT_GST_RATE
      : 0;
    const split = deriveGstSplit(gstRate, memberStateCode, issuerStateCode);
    const cgstAmount = round2((taxableAmount * split.cgst) / 100);
    const sgstAmount = round2((taxableAmount * split.sgst) / 100);
    const igstAmount = round2((taxableAmount * split.igst) / 100);
    cgstTotal = round2(cgstTotal + cgstAmount);
    sgstTotal = round2(sgstTotal + sgstAmount);
    igstTotal = round2(igstTotal + igstAmount);

    // Deposit is a caller-resolved line total (matrix tier or baseline×qty),
    // untouched by discounts/GST.
    const securityDeposit = round2(Math.max(0, r.line.securityDepositLineTotal || 0));
    securityDepositTotal = round2(securityDepositTotal + securityDeposit);
    // An ended plan no longer recurs, so it contributes nothing to the
    // forward "Monthly (recurring)" figure.
    if (r.line.recurring && !input.endDate) {
      monthlyRecurringTotal = round2(monthlyRecurringTotal + r.lineMonthly + r.sundayMonthly);
    }

    return {
      planId: r.line.planId,
      description: r.line.description,
      quantity: r.qty,
      durationMonths: r.line.recurring ? Math.max(1, r.line.durationMonths || 1) : 0,
      unitPrice: round2(r.line.unitPrice),
      lineDiscountPct: r.line.lineDiscountPct || 0,
      lineMonthly: r.lineMonthly,
      proratedFirstAmount: r.stub,
      taxableAmount,
      gstRate,
      cgstRate: split.cgst,
      cgstAmount,
      sgstRate: split.sgst,
      sgstAmount,
      igstRate: split.igst,
      igstAmount,
      securityDeposit,
      sundayAccessMonthly: r.sundayMonthly,
      sundayAccessTaxable: r.sundayTaxable,
      lineTotal: round2(taxableAmount + cgstAmount + sgstAmount + igstAmount),
    };
  });

  // Keeps the documented invariant "== sum of per-line taxableAmount" true now
  // that each line carries its Sunday share, and so flows into GST + grandTotal.
  const taxableAfterDiscounts = round2(afterMode + sundayAccessTotal);
  const grandTotal = round2(
    taxableAfterDiscounts + cgstTotal + sgstTotal + igstTotal + securityDepositTotal,
  );
  // Nearest-rupee round-off on the WHOLE grandTotal (deposit included). Component
  // figures above are untouched; only the printed final payable becomes rounded.
  const { roundedTotal: roundedGrandTotal, roundOff } = computeRoundOff(grandTotal);

  return {
    lines: resultLines,
    subtotal,
    grossSubtotal,
    planDiscountTotal,
    planDiscountPct,
    additionalDiscountPct: addlPct,
    additionalDiscountAmount,
    paymentModeDiscountPct: modePct,
    paymentModeDiscountAmount,
    totalDiscount: round2(additionalDiscountAmount + paymentModeDiscountAmount),
    taxableAfterDiscounts,
    cgstTotal,
    sgstTotal,
    igstTotal,
    securityDepositTotal,
    sundayAccessTotal,
    monthlyRecurringTotal,
    grandTotal,
    roundOff,
    roundedGrandTotal,
  };
}
